/* source-map.js — ED-02 element layer for the review code editor.
   Pure, dependency-free ESM (no React, no DOM) so Node can unit-test it directly:
   `node --test frontend/src/lib/source-map.test.mjs`.

   Three layers:
     1. indexHtmlSource(html)  — a forgiving tokenizer that builds an element tree with
        char-offset ranges + line:col conversion. Handles comments, doctype/declarations,
        void tags, raw-text elements (<style>/<script>), and quoted attribute values.
        Comments are NODES (never parsed through), so MSO ghost tables can't shift counts.
     2. The band helpers (buildSectionMap / deriveSections / walkBands / injectSections /
        secAtLine / secLabel) — moved VERBATIM from editor.jsx so the editor, the
        inspector injector, and the tests all share one implementation.
     3. buildElementIndex(html) — two-tier selection model: bands (the data-sec rows,
        authored or derived) + sub-elements (content cells, CTAs, images, headings,
        text blocks, rules/spacers) enumerated in document order with elKeys
        "s03/e02". The iframe inspector walks the live DOM with the SAME predicate and
        the SAME order, so elKey ↔ range is an index-zip validated by count, never a guess.
*/

/* ───────────────────────────── tokenizer ───────────────────────────── */

const VOID_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
const RAW_TEXT_TAGS = new Set(['script', 'style', 'title', 'textarea']);

export function indexHtmlSource(html) {
  const text = String(html ?? '');
  const n = text.length;
  const lineStarts = [0];
  for (let i = 0; i < n; i++) if (text.charCodeAt(i) === 10) lineStarts.push(i + 1);

  const nodes = [];            // every node; id = array index
  const roots = [];
  const stack = [];            // open element ids

  const posOf = (off) => {
    let lo = 0, hi = lineStarts.length - 1;
    while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (lineStarts[mid] <= off) lo = mid; else hi = mid - 1; }
    return { line: lo, ch: off - lineStarts[lo] };
  };

  const pushNode = (node) => {
    const id = nodes.length;
    node.id = id;
    node.children = [];
    node.parent = stack.length ? stack[stack.length - 1] : null;
    nodes.push(node);
    if (node.parent != null) nodes[node.parent].children.push(id); else roots.push(id);
    return id;
  };

  let i = 0;
  while (i < n) {
    const lt = text.indexOf('<', i);
    if (lt === -1) {
      pushNode({ kind: 'text', start: i, end: n });
      break;
    }
    if (lt > i) pushNode({ kind: 'text', start: i, end: lt });

    // comment — stored as a node (band labels read them) but never parsed through,
    // so <!--[if mso]><table>…<![endif]--> ghosts never enter the element tree.
    if (text.startsWith('<!--', lt)) {
      const e = text.indexOf('-->', lt + 4);
      const end = e === -1 ? n : e + 3;
      pushNode({ kind: 'comment', start: lt, end });
      i = end; continue;
    }
    // doctype / declaration / processing instruction — skipped, not an element.
    if (text.startsWith('<!', lt) || text.startsWith('<?', lt)) {
      const gt = text.indexOf('>', lt + 2);
      const end = gt === -1 ? n : gt + 1;
      pushNode({ kind: 'decl', start: lt, end });
      i = end; continue;
    }
    // close tag — pop to the nearest matching open (unclosed inners close with it).
    if (text.startsWith('</', lt)) {
      const gt = text.indexOf('>', lt + 2);
      const end = gt === -1 ? n : gt + 1;
      const m = /^<\/\s*([a-zA-Z][\w:-]*)/.exec(text.slice(lt, end));
      if (m) {
        const tag = m[1].toLowerCase();
        let j = stack.length - 1;
        while (j >= 0 && nodes[stack[j]].tag !== tag) j--;
        if (j >= 0) {
          while (stack.length > j) {
            const id = stack.pop();
            nodes[id].closeStart = lt;
            nodes[id].closeEnd = end;
          }
        }
      }
      i = end; continue;
    }
    // open tag
    const om = /^<([a-zA-Z][\w:-]*)/.exec(text.slice(lt, Math.min(n, lt + 256)));
    if (!om) {                       // a stray '<' — literal text char
      pushNode({ kind: 'text', start: lt, end: lt + 1 });
      i = lt + 1; continue;
    }
    const tag = om[1].toLowerCase();
    // quote-aware scan for the closing '>' (a '>' inside a quoted attr must not end the tag)
    let k = lt + om[0].length, q = 0, gt = -1;
    while (k < n) {
      const c = text.charCodeAt(k);
      if (q) { if (c === q) q = 0; }
      else if (c === 34 || c === 39) q = c;
      else if (c === 62) { gt = k; break; }
      k++;
    }
    const openEnd = gt === -1 ? n : gt + 1;
    const rawAttrs = text.slice(lt + om[0].length, gt === -1 ? n : gt);
    const selfClosing = (gt !== -1 && text[gt - 1] === '/') || VOID_TAGS.has(tag);
    const id = pushNode({
      kind: 'el', tag, openStart: lt, openEnd,
      closeStart: null, closeEnd: null, attrs: rawAttrs,
    });
    if (selfClosing) { i = openEnd; continue; }
    if (RAW_TEXT_TAGS.has(tag)) {
      // raw text: content is text up to the matching close tag (never parsed as markup)
      const closeRe = new RegExp('</\\s*' + tag + '\\s*>', 'ig');
      closeRe.lastIndex = openEnd;
      const cm = closeRe.exec(text);
      const textEnd = cm ? cm.index : n;
      stack.push(id);
      if (textEnd > openEnd) pushNode({ kind: 'text', start: openEnd, end: textEnd });
      stack.pop();
      nodes[id].closeStart = cm ? cm.index : null;
      nodes[id].closeEnd = cm ? cm.index + cm[0].length : null;
      i = cm ? cm.index + cm[0].length : n;
      continue;
    }
    stack.push(id);
    i = openEnd;
  }

  return { text, nodes, roots, lineStarts, posOf };
}

/* attribute read from the raw attr string captured by the tokenizer */
export function getAttr(node, name) {
  if (!node || node.kind !== 'el' || !node.attrs) return null;
  const m = new RegExp('(?:^|\\s)' + name + '\\s*=\\s*(?:"([^"]*)"|\'([^\']*)\'|([^\\s>]+))', 'i').exec(node.attrs);
  return m ? (m[1] ?? m[2] ?? m[3] ?? '') : null;
}

/* ── text aggregation (entity-normalized so the DOM side agrees on emptiness) ── */
const ENTITIES = [
  [/&nbsp;|&#160;|&#x0*a0;/gi, ' '],
  [/&amp;/gi, '&'], [/&lt;/gi, '<'], [/&gt;/gi, '>'],
  [/&quot;/gi, '"'], [/&#0*39;|&apos;/gi, "'"],
];
export function decodeEntities(s) {
  let out = String(s ?? '');
  for (const [re, rep] of ENTITIES) out = out.replace(re, rep);
  return out;
}

export function nodeText(idx, id) {
  let out = '';
  const walk = (nid) => {
    const nd = idx.nodes[nid];
    if (nd.kind === 'text') { out += idx.text.slice(nd.start, nd.end); return; }
    if (nd.kind !== 'el') return;              // comments/decls carry no visible text
    for (const c of nd.children) walk(c);
  };
  walk(id);
  return decodeEntities(out);
}

/* fnv-1a 32-bit → 8 hex chars — the comment-identity textHash (stable, $0) */
export function textHash(s) {
  let h = 0x811c9dc5;
  const t = String(s ?? '');
  for (let i = 0; i < t.length; i++) {
    h ^= t.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/* ───────────────────── band helpers (moved verbatim from editor.jsx) ─────────────────────
   Two ways a build exposes sections:
   (a) AUTHORED — the HTML already carries data-sec anchors. buildSectionMap scans them directly.
   (b) DERIVED (PY-36) — a real swarm build carries NO data-sec, so deriveSections recovers the
       bands from the house shell: the direct-child <tr> of the single `em_main_table` content
       table. The derived map feeds BOTH the source line-ranges and injectSections, which stamps
       data-sec into the iframe RENDER COPY ONLY — the editor text and the on-disk artifact are
       never mutated (PY-36 acceptance). */
export function buildSectionMap(text) {
  const lines = text.split('\n');
  const secs = [];
  lines.forEach((ln, i) => {
    const m = ln.match(/data-sec="([^"]+)"/);
    if (m) secs.push({ sec: m[1], start: i, startCh: ln.indexOf('<') < 0 ? 0 : ln.indexOf('<') });
  });
  if (!secs.length) return deriveSections(text);   // no authored anchors → derive from the shell
  secs.forEach((s, i) => { s.end = (i + 1 < secs.length ? secs[i + 1].start - 1 : lines.length - 1); });
  return secs;
}
export function secAtLine(map, line) {
  const hit = map.find(s => line >= s.start && line <= s.end);
  return hit ? hit.sec : null;
}

/* the single content shell every house build shares; its direct-child <tr> are the bands */
const EM_MAIN_OPEN = /<table\b[^>]*\bclass="[^"]*\bem_main_table\b[^"]*"[^>]*>/i;
// tokens the depth-walk tracks — table/tr bounds AND HTML-comment bounds. Comments matter:
// Outlook MSO conditionals ("ghost tables", `<!--[if mso]><table>…<![endif]-->`) carry
// <table>/<tr> the DOM never renders, so they must NOT shift the band count (Stinger STING #3).
const SEC_TOK = /<!--|-->|<(\/?)(table|tr)\b/gi;
const SEC_COMMENT = /<!--\s*=*\s*([^=<>]+?)\s*=*\s*-->\s*$/;   // <!-- == HERO OFFER BAND == --> → label
// layout shells we peel THROUGH when a shell's only band is a nested wrapper — never a content
// table (mirrors section_render.py's "descend only known layout shells"), so a rate/grid table
// is never mistaken for section bands. Extend if the corpus reveals another wrapper class.
const WRAPPER_SHELL = /<table\b[^>]*\bclass="[^"]*\b(?:em_wrapper|em_main|em_full_wrap|em_body)\b[^"]*"[^>]*>/i;
const titleCase = (s) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/* Walk a shell's direct-child <tr> bands, from its interior start `from` to its depth-0
   </table>. Skips rows nested in sub-tables (depth) AND every tag inside an HTML comment
   (invisible to the DOM). Returns ordered [{start,end}] char spans of each band's <tr>…</tr>. */
export function walkBands(text, from) {
  const tok = new RegExp(SEC_TOK.source, 'gi');
  tok.lastIndex = from;
  const rows = [];
  let depth = 0, inComment = false, curStart = null, m;
  while ((m = tok.exec(text))) {
    if (m[0] === '<!--') { inComment = true; continue; }
    if (m[0] === '-->') { inComment = false; continue; }
    if (inComment) continue;                       // MSO ghost tables / commented markup → not real bands
    const closing = m[1] === '/', tag = m[2].toLowerCase();
    if (tag === 'table') { if (closing) { if (depth === 0) break; depth--; } else depth++; } // depth-0 </table> = shell close
    else if (depth === 0) {                        // a direct-child <tr> band (nested rows are depth>0)
      if (!closing) curStart = m.index;
      else if (curStart != null) { rows.push({ start: curStart, end: text.indexOf('>', m.index) + 1 }); curStart = null; }
    }
  }
  return rows;
}

/* Derive section bands from the em_main_table shell → [{sec,label,start,end,startCh,injectAt}].
   Same {sec,start,end,startCh} shape buildSectionMap returns, so every downstream consumer
   works unchanged. injectAt = char offset just past `<tr`, where injectSections stamps the
   ephemeral anchor. PEELS single-wrapper levels: a shell whose only band is a nested LAYOUT
   table holding the real bands descends into it (mirrors section_render.py) so wrapped builds
   resolve to their real siblings, not one giant section. */
export function deriveSections(text) {
  const tm = EM_MAIN_OPEN.exec(text);
  if (!tm) return [];                              // unknown shell → graceful no-op (link stays off)
  const lines = text.split('\n');
  const lineAt = (idx) => {                        // char offset → {line, ch}
    let line = 0, off = 0;
    while (line + 1 < lines.length && off + lines[line].length + 1 <= idx) { off += lines[line].length + 1; line++; }
    return { line, ch: idx - off };
  };
  let rows = [], from = tm.index + tm[0].length;   // scan just inside em_main_table
  for (let peel = 0; peel < 6; peel++) {           // descend single-wrapper LAYOUT levels
    rows = walkBands(text, from);
    if (rows.length !== 1) break;                  // real siblings (or none) → done
    const inner = WRAPPER_SHELL.exec(text.slice(rows[0].start, rows[0].end));
    if (!inner) break;                             // a genuine single band, not a layout wrapper → keep it
    from = rows[0].start + inner.index + inner[0].length;
  }
  const secs = rows.map((r, i) => {
    const pos = lineAt(r.start);
    const cm = SEC_COMMENT.exec(text.slice(0, r.start));   // preceding <!-- == NAME == -->, if any
    return {
      sec: 's' + String(i + 1).padStart(2, '0'),
      label: cm ? titleCase(cm[1].trim()) : ('Section ' + (i + 1)),
      start: pos.line, startCh: pos.ch, injectAt: r.start + 3,   // just past "<tr"
    };
  });
  secs.forEach((s, i) => { s.end = (i + 1 < secs.length ? secs[i + 1].start - 1 : lines.length - 1); });
  return secs;
}

/* A copy of the build HTML with data-sec stamped onto each derived band — for the iframe
   srcdoc ONLY. Authored builds (already have data-sec) and unknown shells pass through
   untouched, so nothing that worked before changes. Reverse-insert to keep offsets valid. */
export function injectSections(text) {
  if (/data-sec=/.test(text)) return text;
  const secs = deriveSections(text);
  if (!secs.length) return text;
  let out = text;
  for (let i = secs.length - 1; i >= 0; i--) {
    const at = secs[i].injectAt;
    out = out.slice(0, at) + ` data-sec="${secs[i].sec}"` + out.slice(at);
  }
  return out;
}

/* Human labels for the linkable sections (used in the link breadcrumb). */
export const SECTION_LABELS = {
  bar: 'Accent bar',
  logo: 'Logo',
  hero: 'Hero image',
  headline: 'Headline + body',
  ratetable: 'Rate table',
  cta: 'CTA button',
  footer: 'Footer',
};

/* Human label for the link breadcrumb: authored sections have curated labels; derived
   sections carry their own (from the shell comment, else "Section N"). */
export function secLabel(sec, text) {
  if (SECTION_LABELS[sec]) return SECTION_LABELS[sec];
  const hit = buildSectionMap(text).find((s) => s.sec === sec);
  return (hit && hit.label) || sec;
}

/* ───────────────────── element layer (ED-02) ───────────────────── */

const SUB_ALWAYS = new Set(['a', 'img', 'hr']);
const SUB_TEXT = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li']);

function hasDesc(idx, id, pred) {
  const stack = [...idx.nodes[id].children];
  while (stack.length) {
    const nd = idx.nodes[stack.pop()];
    if (nd.kind !== 'el') continue;
    if (pred(nd)) return true;
    stack.push(...nd.children);
  }
  return false;
}

/* The selectable predicate — MUST stay in lockstep with the iframe inspector's DOM port
   (preview-doc.js INSPECTOR_JS). Both sides enumerate in document order; the manifest
   count-check (zipElements) catches any drift and degrades to band-only, never guesses. */
function isSelectableElement(idx, id) {
  const nd = idx.nodes[id];
  const tag = nd.tag;
  if (SUB_ALWAYS.has(tag)) return true;
  const txt = nodeText(idx, id);
  if (SUB_TEXT.has(tag)) return txt.trim().length > 0 || hasDesc(idx, id, (x) => x.tag === 'img' || x.tag === 'a');
  if (tag === 'td') {
    if (hasDesc(idx, id, (x) => x.tag === 'table')) return false;   // layout container, not content
    return txt.length > 0 || nd.children.some((c) => idx.nodes[c].kind === 'el');
  }
  if (tag === 'div') return txt.trim().length > 0;
  return false;
}

function elementLabel(idx, id) {
  const nd = idx.nodes[id];
  const t = nodeText(idx, id).replace(/\s+/g, ' ').trim();
  const snip = t.length > 28 ? t.slice(0, 27) + '…' : t;
  switch (nd.tag) {
    case 'a': return snip ? `Link · ${snip}` : 'CTA link';
    case 'img': {
      const alt = (getAttr(nd, 'alt') || '').trim();
      if (alt) return `Image · ${alt.length > 24 ? alt.slice(0, 23) + '…' : alt}`;
      const src = getAttr(nd, 'src') || '';
      const base = src.split('/').pop() || 'image';
      return `Image · ${base}`;
    }
    case 'hr': return 'Divider';
    case 'td': return snip || 'Spacer / rule';
    default: return snip || `<${nd.tag}>`;
  }
}

function rangeOf(idx, id) {
  const nd = idx.nodes[id];
  const end = nd.closeEnd ?? nd.openEnd;
  const sp = idx.posOf(nd.openStart);
  const ep = idx.posOf(end);
  return { start: nd.openStart, end, startLine: sp.line, startCh: sp.ch, endLine: ep.line, endCh: ep.ch };
}

/* Build the full two-tier element index for an editor buffer.
   Returns { ok, reason?, bands:[{sec,label,elKey,tag,range,textHash}], elements:[…all incl. bands…] }.
   elKeys: bands = the sec itself ("s03"); sub-elements = "<sec>/e<NN>" in document order.
   `range` offsets are into the EXACT string passed in (the editor buffer). */
export function buildElementIndex(html) {
  const text = String(html ?? '');
  const idx = indexHtmlSource(text);
  const secs = buildSectionMap(text);
  if (!secs.length) return { ok: false, reason: 'no-shell', bands: [], elements: [], idx };

  const byOffset = new Map();
  idx.nodes.forEach((nd) => { if (nd.kind === 'el') byOffset.set(nd.openStart, nd.id); });

  const bands = [];
  const elements = [];
  for (const s of secs) {
    // derived sections pin their <tr> via injectAt (char just past "<tr"); authored sections
    // pin via the data-sec attribute itself. Prefer the offset (exact), fall back to attr scan.
    let bandId = null;
    if (s.injectAt != null) {
      const hit = byOffset.get(s.injectAt - 3);
      if (hit != null && idx.nodes[hit].tag === 'tr') bandId = hit;
    }
    if (bandId == null) {
      const hit = idx.nodes.find((nd) => nd.kind === 'el' && getAttr(nd, 'data-sec') === s.sec);
      if (hit) bandId = hit.id;
    }
    if (bandId == null) continue;
    const label = s.label || s.sec;
    const bandEntry = {
      elKey: s.sec, kind: 'band', band: s.sec, tag: idx.nodes[bandId].tag, label,
      range: rangeOf(idx, bandId), parentKey: null, ordinal: 0,
      textHash: textHash(nodeText(idx, bandId).replace(/\s+/g, ' ').trim()),
    };
    bands.push({ sec: s.sec, label, elKey: s.sec, range: bandEntry.range });
    elements.push(bandEntry);

    let ord = 0;
    const walk = (id, parentKey) => {
      for (const cid of idx.nodes[id].children) {
        const c = idx.nodes[cid];
        if (c.kind !== 'el') continue;
        if (getAttr(c, 'data-sec') != null) continue;     // a nested band's subtree — never double-count
        if (isSelectableElement(idx, cid)) {
          ord++;
          const elKey = s.sec + '/e' + String(ord).padStart(2, '0');
          elements.push({
            elKey, kind: 'element', band: s.sec, tag: c.tag, label: elementLabel(idx, cid),
            range: rangeOf(idx, cid), parentKey, ordinal: ord,
            textHash: textHash(nodeText(idx, cid).replace(/\s+/g, ' ').trim()),
          });
          walk(cid, elKey);          // selectable parents still expose their children (Figma nesting)
        } else {
          walk(cid, parentKey);      // transparent layout container — children join the nearest selectable
        }
      }
    };
    walk(bandId, s.sec);
  }
  return { ok: bands.length > 0, reason: bands.length ? null : 'no-bands', bands, elements, idx };
}

/* Validate the iframe's enumeration against ours. The inspector posts
   {bands, total, perBand:{sec:count}}; ANY divergence → degrade to band-level linking
   (honest) rather than risk jumping to the wrong code. */
export function zipElements(elIndex, manifest) {
  if (!elIndex || !elIndex.ok || !manifest || typeof manifest !== 'object') {
    return { ok: false, bandOnly: true, reason: 'no-manifest' };
  }
  const subTotal = elIndex.elements.filter((e) => e.kind === 'element').length;
  const perBand = {};
  for (const e of elIndex.elements) {
    if (e.kind !== 'element') continue;
    perBand[e.band] = (perBand[e.band] || 0) + 1;
  }
  const confidence = {};
  let ok = manifest.bands === elIndex.bands.length && manifest.total === subTotal;
  for (const b of elIndex.bands) {
    const theirs = manifest.perBand ? manifest.perBand[b.sec] : undefined;
    confidence[b.sec] = theirs === perBand[b.sec] ? 'zip' : 'band-only';
    if (theirs !== perBand[b.sec]) ok = false;
  }
  return { ok, bandOnly: !ok, confidence, expected: { bands: elIndex.bands.length, total: subTotal }, got: { bands: manifest.bands, total: manifest.total } };
}

/* Deepest element whose range contains the caret (code → preview direction). */
export function elementAtOffset(elIndex, offset) {
  if (!elIndex || !elIndex.ok) return null;
  let best = null;
  for (const e of elIndex.elements) {
    if (offset >= e.range.start && offset <= e.range.end) {
      if (!best || (e.range.end - e.range.start) <= (best.range.end - best.range.start)) best = e;
    }
  }
  return best;
}

export function elementByKey(elIndex, elKey) {
  if (!elIndex || !elIndex.ok || !elKey) return null;
  return elIndex.elements.find((e) => e.elKey === elKey) || null;
}
